import { Box, Flex } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import SearchComponent from 'UiKit/search';
import FilterSelect from 'UiKit/filter-select';
import { stockPromoText } from 'Const/stock-promo/view';
import { TextBold } from 'UiKit/typography';
import ButtonOutline from 'UiKit/button/buttonOutline';
import { RefreshIcon } from 'Asset/icon';
import { Condition } from 'Component/others';
import useGetBrand from 'Domain/brand/useGetBrand';
import useGetStockStatus from 'Domain/stock-promo/useGetStockStatus';
import useGetPromoStatus from 'Domain/stock-promo/useGetPromoStatus';
import useGetAccountPromo from 'Domain/stock-promo/useGetAccountPromo';

import { useStockPromoState } from 'View/stock-promo/state';

import styles from './styles.module.css';

const Filter = ({ data, isFetching }) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('stock-promo');
  const { updateFilterState, filterState, selectedTab } = useStockPromoState();
  const { brand, statusStock, search, shop, promoStatus } = filterState;

  const { data: brandOptions, isLoading: isLoadingBrand } = useGetBrand();
  const { data: stockStatusOptions, isLoading: isLoadingStockStatus } = useGetStockStatus();
  const { data: promoStatusOptions, isLoading: isLoadingPromoStatus } = useGetPromoStatus();
  const { data: accountPromoOptions, isLoading: isLoadingAccountPromo } = useGetAccountPromo();

  const handleRefresh = () => {
    const queryKey = selectedTab === 'promo' ? 'promo-list' : 'stock-list';
    queryClient.invalidateQueries({ queryKey: [queryKey, filterState] });
  };

  const onChangeFilter = (newFilter) => updateFilterState({ ...newFilter, page: 1 });

  return (
    <Box className={styles['filter__container']}>
      <Box className={styles['filter__section--left']}>
        <Flex gap={'12px'} flex={1}>
          <SearchComponent maxWidth="250px" value={search} debounceTime={600} onSearch={(search) => onChangeFilter({ search })} />
          <Box>
            <FilterSelect
              placeholder={t('filter.chooseStockStatus')}
              value={statusStock}
              clearable={true}
              onChange={(statusStock) => onChangeFilter({ statusStock })}
              options={stockStatusOptions}
              isLoading={isLoadingStockStatus}
              isMulti={false}
              width="150px"
            />
          </Box>
          <Condition statement={selectedTab === 'promo'}>
            <Box>
              <FilterSelect
                placeholder={t('filter.choosePromoStatus')}
                value={promoStatus}
                clearable={true}
                onChange={(promoStatus) => onChangeFilter({ promoStatus })}
                options={promoStatusOptions}
                isLoading={isLoadingPromoStatus}
                width="160px"
                isMulti={true}
                counterLabel={'Status Promo'}
              />
            </Box>
          </Condition>
          <Box>
            <FilterSelect
              placeholder={t('filter.chooseBrand')}
              options={brandOptions}
              isLoading={isLoadingBrand}
              value={brand}
              clearable={true}
              onChange={(brand) => onChangeFilter({ brand })}
              width="150px"
              isMulti={true}
              counterLabel={'Brand'}
            />
          </Box>
          <Condition statement={selectedTab === 'promo'}>
            <Box>
              <FilterSelect
                placeholder={t('filter.chooseStore')}
                options={accountPromoOptions}
                isLoading={isLoadingAccountPromo}
                value={shop}
                clearable={true}
                onChange={(shop) => onChangeFilter({ shop })}
                width="160px"
                isMulti={true}
                counterLabel={'Toko'}
              />
            </Box>
          </Condition>
        </Flex>
      </Box>
      <Box className={styles['filter__section--right']}>
        <Box>
          <TextBold className={`${styles['filter__last-sync--text']} ${isFetching ? styles['filter__last-sync--load'] : ''}`}>
            {stockPromoText.lastSyncText}
          </TextBold>
          <TextBold className={`${styles['filter__last-sync--text']} ${isFetching ? styles['filter__last-sync--load'] : ''}`}>{data?.lastSync}</TextBold>
        </Box>
        <ButtonOutline
          height={'28px'}
          width={'32px'}
          borderWidth={'1.5px'}
          onClick={handleRefresh}
          disabled={isFetching}
          className={styles['filter__button--refresh']}
        >
          <RefreshIcon size="16px" color={isFetching ? 'var(--color-semantic-text-disabled)' : 'var(--color-primary60)'} />
        </ButtonOutline>
      </Box>
    </Box>
  );
};

export default Filter;
